import { createColorStack } from './colorStack';
import chroma from 'chroma-js';

const ORANGE = 'orange';
const PINK = 'pink';
const PURPLE = 'purple';
const RED = 'red';
const BLUE = 'blue';
const GREEN = 'green';
export const TEAL = 'teal';
export const ROYAL_BLUE = 'royalblue';
export const MAGENTA = 'magenta';

/**
 * createAccessoryPaletteFromSeed creates 5 accessory color stacks and a midpoint value given a specific color seed.
 * The seed color - must be a valid color, but format can be any format supported by chroma.js
 * @param {*} seedColor
 */
export function createAccessoryPaletteFromSeed(seedColor) {
  if (chroma.valid(seedColor)) {
    let palette = [];
    // If the seed color is white, black, or pure grey, theme builder will use the default carina palette for accessory colors
    // eslint-disable-next-line no-unused-vars
    const [_, s, l] = chroma(seedColor).hsl();
    if (s === 0 || l === 0 || l === 1) {
      // eslint-disable-next-line no-console
      console.error(
        `The seed color ${seedColor} you provided cannot be used to generate a color palette. \n Carina will use the default accessory color palette instead. \n If you would prefer another color, provide one that's not either black, white or grey (having equal levels of rgb).`
      );
      return null;
    }

    palette = [...generateRemainingColors(seedColor)];

    const colorStacks = [
      createColorStack(palette[0], 'accessoryOne'),
      createColorStack(palette[1], 'accessoryTwo'),
      createColorStack(palette[2], 'accessoryThree'),
      createColorStack(palette[3], 'accessoryFour'),
      createColorStack(palette[4], 'accessoryFive')
    ];

    return { colorStacks, seedColor };
  }
}

// Using the seed color and it's position within its color category as a starting point,
// the remaining colors are generated.
function generateRemainingColors(seedColor) {
  const seedHue = Math.round(chroma(seedColor).get('hsl.h'));
  let qualPalette = [seedColor];
  let qualColorCategories = [getColorCategory(seedColor)];
  let nextColorHue = null;
  let nextColorCategory = '';
  let seedHuePosition = 0.1;

  // If seed color is Red, calculate the seedHue position differently
  if (qualColorCategories[0] === RED && seedHue >= 0 && seedHue <= 5) {
    seedHuePosition =
      (seedHue - 0 + 1) / getColorInfo(qualColorCategories[0]).totalHues;
  } else if (!(seedHue >= 36 && seedHue <= 99)) {
    // if any color other than yellow
    seedHuePosition =
      (seedHue - getColorInfo(qualColorCategories[0]).lowerLimit + 1) /
      getColorInfo(qualColorCategories[0]).totalHues;
  }

  // Generate the remaining colors iteratively
  for (let i = 0; i < 5; i++) {
    // First, identify the next color's category
    nextColorCategory = getNextColorCategoryUsingPreviousHue(
      Math.round(chroma(qualPalette[i]).get('hsl.h')),
      qualColorCategories
    );
    qualColorCategories.push(nextColorCategory);
    // Then, calculate the hue within that category
    nextColorHue = Math.round(
      getColorInfo(nextColorCategory).lowerLimit +
        Math.abs(
          seedHuePosition * getColorInfo(nextColorCategory).totalHues - 1
        )
    );
    // Finally, generate the color and add it to the array
    qualPalette.push(generateNextColor(nextColorHue, nextColorCategory));
  }

  return qualPalette;
}

// Get the color's category based on its hue.
function getColorCategory(color) {
  const colorHue = Math.round(chroma(color).get('hsl.h'));

  switch (true) {
    case colorHue >= 193.0 && colorHue <= 214.0:
      return BLUE;
    case colorHue >= 6.0 && colorHue <= 35.0:
      return ORANGE;
    case colorHue >= 100.0 && colorHue <= 174.0:
      return GREEN;
    case colorHue >= 331.0 && colorHue <= 340.0:
      return PINK;
    case colorHue >= 245.0 && colorHue <= 299.0:
      return PURPLE;
    case colorHue >= 175.0 && colorHue <= 192.0:
      return TEAL;
    case colorHue >= 215.0 && colorHue <= 244.0:
      return ROYAL_BLUE;
    case colorHue >= 300.0 && colorHue <= 330.0:
      return MAGENTA;
    case (colorHue >= 341.0 && colorHue <= 360.0) ||
      (colorHue >= 0 && colorHue <= 5.0):
      return RED;
    default:
      return '';
  }
}

// Get the color category's total hues and lower limit.
function getColorInfo(colorCategory) {
  switch (colorCategory) {
    case BLUE:
      return { totalHues: 22, lowerLimit: 193 };
    case ORANGE:
      return { totalHues: 30, lowerLimit: 6 };
    case GREEN:
      return { totalHues: 75, lowerLimit: 100 };
    case PINK:
      return { totalHues: 10, lowerLimit: 331 };
    case PURPLE:
      return { totalHues: 55, lowerLimit: 245 };
    case TEAL:
      return { totalHues: 18, lowerLimit: 175 };
    case ROYAL_BLUE:
      return { totalHues: 30, lowerLimit: 215 };
    case MAGENTA:
      return { totalHues: 31, lowerLimit: 300 };
    case RED:
      return { totalHues: 26, lowerLimit: 341 };
    default:
      return { totalHues: 22, lowerLimit: 193 };
  }
}

// Get the next color category using the previous color's hue.
function getNextColorCategoryUsingPreviousHue(colorHue, qualColorCategories) {
  switch (true) {
    // Hue's color category: Blue
    case colorHue >= 193.0 && colorHue <= 214.0: {
      if (!qualColorCategories.includes(ORANGE)) return ORANGE;
      return GREEN;
    }
    // Hue's color category: Orange
    case colorHue >= 6.0 && colorHue <= 35.0: {
      if (!qualColorCategories.includes(BLUE)) return BLUE;
      if (!qualColorCategories.includes(GREEN)) return GREEN;
      return PURPLE;
    }
    // Hue's color category: Green
    case colorHue >= 100.0 && colorHue <= 174.0: {
      if (!qualColorCategories.includes(PINK)) return PINK;
      return BLUE;
    }
    // Hue's color category: Pink
    case colorHue >= 331.0 && colorHue <= 340.0: {
      if (!qualColorCategories.includes(GREEN)) return GREEN;
      if (!qualColorCategories.includes(BLUE)) return BLUE;
      return PURPLE;
    }
    // Hue's color category: Purple
    case colorHue >= 245.0 && colorHue <= 299.0: {
      if (!qualColorCategories.includes(GREEN)) return GREEN;
      return TEAL;
    }
    // Hue's color category: Teal
    case colorHue >= 175.0 && colorHue <= 192.0: {
      if (!qualColorCategories.includes(ROYAL_BLUE)) return ROYAL_BLUE;
      return MAGENTA;
    }
    // Hue's color category: Royal Blue
    case colorHue >= 215.0 && colorHue <= 244.0: {
      if (!qualColorCategories.includes(TEAL)) return TEAL;
      if (!qualColorCategories.includes(MAGENTA)) return MAGENTA;
      return ORANGE;
    }
    // Hue's color category: Magenta
    case colorHue >= 300.0 && colorHue <= 330.0: {
      if (!qualColorCategories.includes(TEAL)) return TEAL;
      return BLUE;
    }
    // Hue's color category: Red
    case (colorHue >= 341.0 && colorHue <= 360.0) ||
      (colorHue >= 0 && colorHue <= 5.0): {
      return GREEN;
    }
    // Default includes shades of yellow
    default: {
      return BLUE;
    }
  }
}

// Generate the color based on the color category and hue. The generated colors have a 3:1 ratio with background.
function generateNextColor(colorHue, colorCategory) {
  switch (colorCategory) {
    case BLUE: {
      if (colorHue >= 193 && colorHue <= 203)
        return chroma(colorHue, 0.69, 0.45, 'hsl').hex();
      return chroma(colorHue, 0.69, 0.54, 'hsl').hex();
    }
    case ORANGE: {
      if (colorHue >= 6 && colorHue <= 14) {
        return chroma(colorHue, 0.65, 0.5, 'hsl').hex();
      } else if (colorHue >= 15 && colorHue <= 20) {
        return chroma(colorHue, 0.79, 0.57, 'hsl').hex();
      } else if (colorHue >= 21 && colorHue <= 25) {
        return chroma(colorHue, 0.73, 0.53, 'hsl').hex();
      } else if (colorHue >= 26 && colorHue <= 30) {
        return chroma(colorHue, 0.8, 0.48, 'hsl').hex();
      }
      return chroma(colorHue, 0.9, 0.44, 'hsl').hex();
    }
    case GREEN: {
      if (colorHue >= 100 && colorHue <= 110) {
        return chroma(colorHue, 0.47, 0.43, 'hsl').hex();
      } else if (colorHue >= 111 && colorHue <= 120) {
        return chroma(colorHue, 0.47, 0.4, 'hsl').hex();
      } else if (colorHue >= 121 && colorHue <= 150) {
        return chroma(colorHue, 0.45, 0.38, 'hsl').hex();
      } else if (colorHue >= 151 && colorHue <= 165) {
        return chroma(colorHue, 0.46, 0.37, 'hsl').hex();
      }
      return chroma(colorHue, 0.55, 0.34, 'hsl').hex();
    }
    case PINK:
      return chroma(colorHue, 0.74, 0.45, 'hsl').hex();
    case PURPLE: {
      if (colorHue >= 245 && colorHue <= 270) {
        return chroma(colorHue, 0.55, 0.58, 'hsl').hex();
      } else if (colorHue >= 271 && colorHue <= 280) {
        return chroma(colorHue, 0.53, 0.56, 'hsl').hex();
      } else if (colorHue >= 281 && colorHue <= 285) {
        return chroma(colorHue, 0.39, 0.51, 'hsl').hex();
      }
      return chroma(colorHue, 0.38, 0.42, 'hsl').hex();
    }
    case TEAL: {
      if (colorHue >= 175 && colorHue <= 185)
        return chroma(colorHue, 0.65, 0.38, 'hsl').hex();
      return chroma(colorHue, 0.65, 0.4, 'hsl').hex();
    }
    case ROYAL_BLUE: {
      if (colorHue >= 215 && colorHue <= 230)
        return chroma(colorHue, 0.68, 0.45, 'hsl').hex();
      return chroma(colorHue, 0.64, 0.45, 'hsl').hex();
    }
    case MAGENTA:
      return chroma(colorHue, 0.74, 0.36, 'hsl').hex();
    default:
      return null;
  }
}
